import { redirect } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/server";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  HardHat,
  MapPin,
  Clock,
  Shield,
  Zap,
  Edit3,
  Wrench,
  Calendar,
  Sparkles,
  Camera,
  Star,
} from "lucide-react";
import { BottomNavWrapper } from "@/components/bottom-nav-wrapper";
import { AvatarUpload } from "@/components/avatar-upload";
import { RatingsSummary } from "@/components/ratings-summary";
import { RatingsList } from "@/components/ratings-list";

export default async function WorkerProfilePage() {
  const supabase = await createClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    redirect("/auth/login");
  }

  const { data: profile } = await supabase
    .from("profiles")
    .select("id, name, avatar_url, suburb, onboarding_completed")
    .eq("id", user.id)
    .single();

  // If onboarding not completed, send to build
  if (!profile?.onboarding_completed) {
    redirect("/worker/profile/build");
  }

  const { data: workerProfile } = await supabase
    .from("worker_profiles")
    .select("profile_id, trades, experience_years, certifications, bio, ai_extracted, availability")
    .eq("profile_id", user.id)
    .single();

  if (!workerProfile) {
    redirect("/worker/profile/build");
  }

  const trades = workerProfile.trades || [];
  const skills = (workerProfile.ai_extracted as { skills?: string[] })?.skills || [];
  const certs = workerProfile.certifications || [];
  const expLabel = getExperienceLabel(workerProfile.experience_years);

  // Fetch ratings with profile information
  const { data: ratings } = await supabase
    .from("ratings")
    .select(`
      *,
      from_profile:profiles!ratings_from_id_fkey(
        id,
        name,
        avatar_url
      )
    `)
    .eq("to_id", user.id)
    .order("created_at", { ascending: false })
    .limit(3); // Show latest 3 ratings

  const avgRating = ratings && ratings.length > 0
    ? (ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length).toFixed(1)
    : null;
  
  const totalRatings = ratings?.length || 0;

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-6">
        {/* Profile header */}
        <div className="flex flex-col items-center text-center">
          <AvatarUpload
            currentUrl={profile.avatar_url}
            userName={profile.name || user.email || ""}
          />

          <h1 className="mt-4 text-2xl font-bold">{profile.name || "Worker"}</h1>

          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            {trades.map((t: string) => (
              <Badge key={t} variant="secondary" className="text-sm">
                <Wrench className="mr-1 h-3 w-3" />
                {t}
              </Badge>
            ))}
          </div>

          <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
            {profile.suburb && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {profile.suburb}
              </span>
            )}
            {expLabel && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {expLabel}
              </span>
            )}
            {avgRating && (
              <span className="flex items-center gap-1">
                ⭐ {avgRating} ({ratings!.length})
              </span>
            )}
          </div>

          {workerProfile.availability && (
            <Badge variant="outline" className="mt-2">
              <Calendar className="mr-1 h-3 w-3" />
              {workerProfile.availability}
            </Badge>
          )}
        </div>

        {/* Bio */}
        {workerProfile.bio && (
          <Card className="mt-6">
            <CardContent className="pt-5">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                About me
              </div>
              <p className="text-base leading-relaxed">{workerProfile.bio}</p>
            </CardContent>
          </Card>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <Card className="mt-3">
            <CardContent className="pt-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Zap className="h-4 w-4" />
                Skills
              </div>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill: string) => (
                  <Badge key={skill} className="text-sm">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Certifications */}
        {certs.length > 0 && (
          <Card className="mt-3">
            <CardContent className="pt-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Shield className="h-4 w-4" />
                Certifications
              </div>
              <div className="space-y-2">
                {certs.map((cert: string) => (
                  <div
                    key={cert}
                    className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  >
                    <Shield className="h-4 w-4 text-primary" />
                    {cert}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ratings */}
        {(ratings && ratings.length > 0) && (
          <div className="mt-6">
            <RatingsSummary 
              averageRating={parseFloat(avgRating || "0")}
              totalRatings={totalRatings}
            />
            
            <div className="mt-4">
              <RatingsList 
                ratings={ratings}
                title="Recent Reviews"
              />
              
              {totalRatings > 3 && (
                <Button asChild variant="outline" className="w-full mt-4">
                  <Link href={`/ratings/${user.id}`}>
                    <Star className="h-4 w-4 mr-2" />
                    View All Reviews ({totalRatings})
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Edit button */}
        <div className="mt-6">
          <Button asChild variant="outline" className="w-full h-12 text-base gap-2">
            <Link href="/worker/profile/build">
              <Edit3 className="h-5 w-5" />
              Edit Profile
            </Link>
          </Button>
        </div>
      </main>

      <BottomNavWrapper userType="worker" />
    </div>
  );
}

function getExperienceLabel(years: number | null): string | null {
  if (years === null || years === undefined) return null;
  if (years === 0) return "< 1 year";
  if (years <= 3) return `${years} years`;
  if (years <= 5) return "3-5 years";
  if (years <= 10) return "5-10 years";
  return "10+ years";
}
